import { SqlDatabase } from "remult";

/** True when coordinates are usable for map/feed geo (not 0,0). */
export function hasValidCoordinates(
  coordinates: unknown,
): coordinates is { lat: number; lng: number; } {
  if (!coordinates || typeof coordinates !== "object" || Array.isArray(coordinates)) {
    return false;
  }
  const { lat, lng } = coordinates as { lat?: unknown; lng?: unknown; };
  if (typeof lat !== "number" || typeof lng !== "number") return false;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  return lat !== 0 || lng !== 0;
}

/** Inverse of {@link hasValidCoordinates} — matches admin "אין קואורדינטות" badge. */
export function hasNoCoordinates(coordinates: unknown): boolean {
  return !hasValidCoordinates(coordinates);
}

/**
 * SQL predicate: opportunity row has no usable map coordinates.
 * Mirrors feed `opportunityEffectiveGeog` (PostGIS + legacy JSON).
 */
export function opportunityHasNoCoordinatesSql(
  coordinatesGeogColumn: string,
  coordinatesJsonColumn: string,
): string {
  return `COALESCE(
    ${coordinatesGeogColumn},
    CASE
      WHEN (${coordinatesJsonColumn}->>'lat')::double precision IS NOT NULL
        AND (${coordinatesJsonColumn}->>'lng')::double precision IS NOT NULL
        AND NOT (
          (${coordinatesJsonColumn}->>'lat')::double precision = 0
          AND (${coordinatesJsonColumn}->>'lng')::double precision = 0
        )
      THEN ST_SetSRID(
        ST_MakePoint(
          (${coordinatesJsonColumn}->>'lng')::double precision,
          (${coordinatesJsonColumn}->>'lat')::double precision
        ),
        4326
      )::geography
      ELSE NULL
    END
  ) IS NULL`;
}

/** Sync PostGIS `coordinates_geog` from JSON `coordinates` after Remult save. */
export async function syncOpportunityCoordinatesGeog(
  opportunityId: string,
  coordinates: unknown,
): Promise<void> {
  const cmd = SqlDatabase.getDb().createCommand();
  if (!hasValidCoordinates(coordinates)) {
    await cmd.execute(
      `UPDATE opportunity
       SET coordinates_geog = NULL
       WHERE id = ${cmd.param(opportunityId)}`,
    );
    return;
  }

  await cmd.execute(
    `UPDATE opportunity
     SET coordinates_geog = ST_SetSRID(ST_MakePoint(${cmd.param(coordinates.lng)}, ${cmd.param(coordinates.lat)}), 4326)::geography
     WHERE id = ${cmd.param(opportunityId)}`,
  );
}

/** Great-circle distance in km between two WGS84 points. */
export function distanceKmBetween(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const R = 6371;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) *
    Math.cos(toRad(lat2)) *
    Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
